import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { CalendarEntry } from './CalendarTypes';

export class JournalService {
  static async fetchJournalEntries(
    companyId: string,
    selectedDate: string,
    selectedUserId: string,
    currentUserId: string,
    isAdmin: boolean,
    isManager: boolean
  ): Promise<CalendarEntry[]> {
    if (!companyId || !selectedDate) return [];

    try {
      const entriesRef = collection(db, `companies/${companyId}/journals`);
      let entriesQuery;

      if (isAdmin) {
        // Admin can see all entries for the selected date and user
        entriesQuery = query(
          entriesRef,
          where('date', '==', selectedDate),
          where('userId', '==', selectedUserId),
          orderBy('startTime', 'asc')
        );
      } else if (isManager) {
        // Manager can see entries for users under them + their own entries
        entriesQuery = query(
          entriesRef,
          where('date', '==', selectedDate),
          where('userId', '==', selectedUserId),
          orderBy('startTime', 'asc')
        );
      } else {
        // Regular user can only see their own entries
        entriesQuery = query(
          entriesRef,
          where('date', '==', selectedDate),
          where('userId', '==', currentUserId),
          orderBy('startTime', 'asc')
        );
      }

      const snapshot = await getDocs(entriesQuery);
      const entriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        entryType: 'journal'
      })) as CalendarEntry[];

      return entriesData;
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      return [];
    }
  }

  static async addJournalEntry(
    companyId: string,
    userId: string,
    userName: string,
    userEmail: string,
    date: string,
    journal: string,
    startTime: string,
    endTime: string
  ): Promise<void> {
    const journalsRef = collection(db, `companies/${companyId}/journals`);
    
    await addDoc(journalsRef, {
      userId,
      userName,
      userEmail,
      date,
      journal,
      startTime,
      endTime,
      createdAt: Timestamp.now(),
      companyId
    });
  }

  static async deleteJournalEntry(companyId: string, entryId: string): Promise<void> {
    await deleteDoc(doc(db, `companies/${companyId}/journals`, entryId));
  }

  static updateTimesBasedOnEntries(entriesData: CalendarEntry[]): { startTime: string; endTime: string } {
    if (entriesData.length > 0) {
      const times = entriesData.map(entry => entry.startTime).sort();
      const lastEndTime = entriesData
        .map(entry => entry.endTime)
        .sort()
        .pop();
      
      const nextStartTime = lastEndTime || '09:00';
      const [hours, minutes] = nextStartTime.split(':').map(Number);
      const nextEndHour = hours + 1;
      const nextEndTime = `${nextEndHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      
      return {
        startTime: nextStartTime,
        endTime: nextEndTime
      };
    }
    
    return {
      startTime: '09:00',
      endTime: '17:00'
    };
  }
}
